// phoneNumber 가 들어오면 숫자 외에 다른 기로를 모두 제거합니다
export function compactPhoneNumber(phoneNumber){
  return phoneNumber.replace(/[^0-9]/g, "");
}

/**
 * 문자열에서 숫자만 추출하여 정수 형태의 문자열로 반환합니다.
 * 음수 부호는 보존됩니다.
 * 예: "-123abc" -> "-123", "456.789" -> "456"
 * @param {string} numberText - 처리할 문자열
 * @return {string} 정수 형태의 문자열
 */
export function compactIntegerNumber(numberText) {
  if (!numberText) return '';
  
  // 입력이 문자열이 아니면 문자열로 변환
  const text = String(numberText);
  
  // 음수 부호 확인
  const isNegative = text.trim().startsWith('-');
  
  // 숫자만 추출 (소수점 제외)
  const numbers = text.replace(/[^0-9]/g, '');
  
  // 음수 부호 추가
  return isNegative ? `-${numbers}` : numbers;
}

export function compactCurrencyNumber(numberText) {
  if (numberText === null || numberText === undefined) return '';
  if (typeof numberText !== 'string') numberText = String(numberText)
  numberText = numberText.trim()
  if (numberText === '') return ''
  // 숫자/소수점/음수만 허용
  let match = numberText.match(/^(-?)(0*)(\d*)(\.?\d*)$/)
  if (!match) return numberText

  let sign = match[1]
  // let zeros = match[2]
  let intPart = match[3]
  let decimalPart = match[4]

  // intPart가 없고 decimalPart만 있으면 0.x 형태로
  if (intPart === '' && decimalPart) intPart = '0'

  // 0만 입력된 경우
  if ((intPart === '' || /^0+$/.test(intPart)) && !decimalPart) return '0'

  // 앞쪽 0 제거
  intPart = intPart.replace(/^0+/, '')
  if (intPart === '') intPart = '0'

  return sign + intPart + decimalPart
}

/**
 * 문자열에서 유효한 실수 형태로 변환합니다.
 * 첫 번째 소수점만 유지하고 나머지는 제거합니다.
 * 음수 부호는 보존됩니다.
 * 예: "-123.456.789" -> "-123.456789", "abc12.34xyz56.78" -> "12.3456.78"
 * @param {string} numberText - 처리할 문자열
 * @return {string} 실수 형태의 문자열
 */
export function compactFloatNumber(numberText) {
  if (!numberText) return '';
  
  // 입력이 문자열이 아니면 문자열로 변환
  const text = String(numberText);
  
  // 음수 부호 확인
  const isNegative = text.trim().startsWith('-');
  
  // 숫자와 첫 번째 소수점만 유지
  let processed = '';
  let hasDecimal = false;
  
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (char >= '0' && char <= '9') {
      processed += char;
    } else if (char === '.' && !hasDecimal) {
      processed += '.';
      hasDecimal = true;
    }
  }
  
  // 음수 부호 추가
  return isNegative ? `-${processed}` : processed;
}


// 한국 번호가 맞는지 확인 82로 시작하거나 지역번호가 올바른경우
export function isKoreaPhoneNumber(phoneNumber){
  const validAreaCodes = [
    // 이동전화
    '010', '011',
    // 공통 서비스
    '020', '030', '050', '060', '070',
    // 지역번호
    '02', '031', '032', '033', '041', '042', '043', '044',
    '051', '052', '053', '054', '055', '061', '062', '063', '064'
  ];

  const compact = compactPhoneNumber(phoneNumber);

  // 국제번호 형식 (82로 시작)
  if (compact.startsWith('82')) {
    const numberWithoutCountryCode = compact.slice(2);
    
    // 국제번호 이후 길이 확인 (일반적으로 9~11자리)
    if (numberWithoutCountryCode.length < 9 || numberWithoutCountryCode.length > 11) {
      return false;
    }

    // 지역번호 추출
    let areaCode = '';
    if (numberWithoutCountryCode.startsWith('2')) { // 서울특별시
      areaCode = '02';
    } else {
      areaCode = numberWithoutCountryCode.slice(0, 3);
    }

    return validAreaCodes.includes(areaCode);
  
  } else {
    // 국내 번호 형식
    if (compact.length < 9 || compact.length > 11) {
      return false;
    }

    // 지역번호 추출
    let areaCode = '';
    if (compact.startsWith('02')) { // 서울특별시
      areaCode = '02';
    } else {
      areaCode = compact.slice(0, 3);
    }

    return validAreaCodes.includes(areaCode);
  }
}

// 한국 내에서 사용 가능한 전화번호 형식을 맞춰줍니다.
// 01012345678 -> 010 1234 5678 형식으로 만들어줍니다.
// 0101234567 -> 010 234 5678 형식으로 만들어줍니다.
// 820101234567 -> 010 1234 5678 형식으로 만들어줍니다.
// 82101234567 -> 010 234 5678 형식으로 만들어집니다.
export function formatInKoreaPhoneNumber(phoneNumber, baseFormat = '$1 $2 $3'){
  const compact = compactPhoneNumber(phoneNumber);
  let formatted = null;

  if (compact.startsWith('82')) {
    if (compact.length === 12) {
      // 예: 820101234567 -> 010 1234 5678
      formatted = compact.replace(/^82(\d{2})(\d{4})(\d{4})$/, `0$1 $2 $3`);
    } else if (compact.length === 11) {
      // 예: 82101234567 -> 010 234 5678
      formatted = compact.replace(/^82(\d{2})(\d{3})(\d{4})$/, `0$1 $2 $3`);
    }
  } else {
    if (compact.length === 11) {
      // 예: 01012345678 -> 010 1234 5678 (010 외 011/016~019 휴대전화 번호도 허용 #164)
      formatted = compact.replace(/^(01[016789])(\d{4})(\d{4})$/, `${baseFormat}`);
    } else if (compact.length === 10) {
      // 예: 0101234567 -> 010 234 5678
      formatted = compact.replace(/^(01[016789])(\d{3})(\d{4})$/, `${baseFormat}`);
    }
  }

  return formatted;
}

/**
 * 숫자를 천 단위 콤마로 포맷팅합니다.
 * 예: 249603 -> "249,603", 1234.56 -> "1,234.56"
 * @param {string|number} numberText - 포맷팅할 숫자
 * @return {string} 콤마로 포맷팅된 문자열
 */
export function formatCurrencyNumber(numberText) {
  if (numberText === null || numberText === undefined) return '';
  
  // compactCurrencyNumber로 먼저 정리
  const cleaned = compactCurrencyNumber(numberText);
  if (!cleaned || cleaned === '0') return cleaned;
  
  // 음수 부호와 소수점 분리
  const isNegative = cleaned.startsWith('-');
  const absoluteValue = isNegative ? cleaned.slice(1) : cleaned;
  const [integerPart, decimalPart] = absoluteValue.split('.');
  
  // 정수부에 콤마 추가
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  // 결과 조합
  let result = formattedInteger;
  if (decimalPart !== undefined) result += '.' + decimalPart;
  if (isNegative) result = '-' + result;
  
  return result;
}
